import { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useImportExcelMutation } from '../hooks/useRetailersQuery';
import { retailersService } from '../services/retailers.service';

interface RetailersImportDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function RetailersImportDialog({
  open,
  onClose,
}: RetailersImportDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<{
    success: boolean;
    imported: number;
    updated: number;
    errors: string[];
  } | null>(null);

  const importMutation = useImportExcelMutation();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    try {
      const importResult = await importMutation.mutateAsync(file);
      setResult(importResult);
    } catch (err) {
      console.error('Import failed:', err);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await retailersService.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'retailers_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Template download failed:', err);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Importar Retailers</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />

          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <UploadFileIcon sx={{ fontSize: 48, color: 'action.active', mb: 2 }} />
            <Typography variant="body1" gutterBottom>
              {file ? file.name : 'Haz clic para seleccionar un archivo Excel'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Formatos soportados: .xlsx, .xls
            </Typography>
          </Box>

          <Button
            size="small"
            onClick={handleDownloadTemplate}
            sx={{ mt: 2 }}
          >
            Descargar plantilla
          </Button>

          {result && (
            <Box sx={{ mt: 3 }}>
              <Alert severity={result.success ? 'success' : 'warning'} sx={{ mb: 2 }}>
                {result.success
                  ? `Importación completada: ${result.imported} nuevos, ${result.updated} actualizados`
                  : 'Importación con errores'}
              </Alert>
              {result.errors.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="error" gutterBottom>
                    Errores:
                  </Typography>
                  <List dense>
                    {result.errors.map((error, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={error} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cerrar</Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!file || importMutation.isPending}
        >
          {importMutation.isPending ? 'Importando...' : 'Importar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
